import React from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Refresh as RefreshIcon, Autorenew as AutorenewIcon } from '@mui/icons-material';
import {
  useGridAutoRefresh,
  DEFAULT_AUTO_REFRESH_INTERVAL_MS,
} from './useGridAutoRefresh';

export interface GridRefreshControlsProps {
  onRefresh: () => void;
  refreshing?: boolean;
  intervalMs?: number;
  size?: 'small' | 'medium';
  forceAutoRefresh?: boolean;
}

export const GridRefreshControls: React.FC<GridRefreshControlsProps> = ({
  onRefresh,
  refreshing = false,
  intervalMs = DEFAULT_AUTO_REFRESH_INTERVAL_MS,
  size = 'small',
  forceAutoRefresh = false,
}) => {
  const { autoRefresh, toggleAutoRefresh } = useGridAutoRefresh(onRefresh, {
    intervalMs,
    forceOn: forceAutoRefresh,
  });

  const intervalSeconds = Math.round(intervalMs / 1000);
  const autoRefreshTooltip = forceAutoRefresh
    ? `Auto-refresh on — every ${intervalSeconds}s while updates are pending`
    : autoRefresh
      ? `Auto-refresh on — every ${intervalSeconds}s (click to turn off)`
      : `Auto-refresh off (click to refresh every ${intervalSeconds}s)`;
  const spinnerSize = size === 'small' ? 18 : 22;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Refresh">
        {/* span keeps the tooltip working while the button is disabled */}
        <span>
          <IconButton
            size={size}
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh"
          >
            {refreshing ? <CircularProgress size={spinnerSize} /> : <RefreshIcon />}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={autoRefreshTooltip}>
        <span>
          <IconButton
            size={size}
            onClick={toggleAutoRefresh}
            disabled={forceAutoRefresh}
            color={autoRefresh ? 'primary' : 'default'}
            aria-label="Toggle auto-refresh"
            aria-pressed={autoRefresh}
            sx={
              autoRefresh
                ? {
                    '@keyframes gridAutoRefreshSpin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' },
                    },
                    '& svg': { animation: 'gridAutoRefreshSpin 2s linear infinite' },
                  }
                : undefined
            }
          >
            <AutorenewIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default GridRefreshControls;
